import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  ChangeRequestDialog,
  ChangeRequestForm,
  ContentTemplate,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  prepareFiles,
  Progress,
  Typography,
} from 'web-library';
import React, { useEffect, useState } from 'react';
import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import {
  FunctionModule,
  RequestTypeMode,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { UploadedFile } from 'web-library/models/UploadedFile';
import editGroupAccessConfigurationSchema from 'web-library/models/UserGroupAccessConfiguration/editGroupAccessConfigurationSchema';
import { useHistory } from 'react-router-dom';
import { MutationStatus } from 'react-query';
import { FUNCTION_NAMES } from 'web-library/models/AuthorityFunctions';
import { usePostSearchApprovers } from '../../../api-refactored/user-group-access-configuration/usePostSearchApprovers';
import { mapApproversToDropdown } from '../CreateEditViewUserGroup';
import { useGetRequestFileConfigs } from '../../../api-refactored/user-group-access-configuration/useGetRequestFileConfigs';
import { RequestFileDropZone } from '../../../components/access-configuration/RequestFileDropZone';
import { useDeleteUserGroup } from '../../../api-refactored/user-group-access-configuration/useDeleteUserGroup';
import { usePostRequestFilePresigned } from '../../../api-refactored/user-group-access-configuration/usePostRequestFilePresigned';
import RouteKey from '../../../routes/key';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';

const { admin } = RouteKey.child;
const { userGroupAccessConfiguration } = RouteKey.admin.child;

const useStyles = makeStyles()((theme) => ({
  statementApproverRemarksField: {
    marginBottom: theme.spacing(3),
  },
  buttonContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  errorText: {
    textAlign: 'end',
    color: theme.color.state.stateError,
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

interface DeleteUserGroupModalProps {
  modalState: boolean;
  setOpenModalState: React.Dispatch<React.SetStateAction<boolean>>;
  deleteUserGroupName: string;
  deleteUserUuid: string;
}

const DeleteUserGroupModal = ({
  modalState,
  setOpenModalState,
  deleteUserGroupName,
  deleteUserUuid: uuid,
}: DeleteUserGroupModalProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const history = useHistory();

  const handleCloseDeleteUserGroupModal = () => {
    setOpenModalState(false);
    setShouldSubmit(false);
    setFiles([]);
    setApiButtonStatus('idle');
    setUploadFileErrorMessage('');
    setErrorMessage('');
  };
  const { data: requestFilesConfigData } = useGetRequestFileConfigs({});

  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState<string>('');

  const [files, setFiles] = useState<UploadedFile[]>([]);

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

  const [confirmChangesModal, setConfirmChangesModal] = useState<boolean>(false);

  const formHook = useForm({
    mode: 'onSubmit',
    resolver: joiResolver(editGroupAccessConfigurationSchema()),
  });

  const { handleSubmit, getValues } = formHook;

  const { data: authorities } = useGetUserAuthorities();

  const {
    mutate: mutatePostSearchApprovers,
    data: postSearchApproversData,
    isLoading: postSearchApproversIsLoading,
  } = usePostSearchApprovers({});

  const handleDeleteBackToListing = () =>
    history.push(`/${admin}/${userGroupAccessConfiguration.route}`, {
      requestType: RequestTypeMode.AdminUserGroup,
      status: 'success',
      event: 'ADD',
    });

  const { mutate: mutateDeleteUserGroup } = useDeleteUserGroup({
    uuid,
    onSuccess: () => {
      setConfirmChangesModal(false);
      handleCloseDeleteUserGroupModal();
      handleDeleteBackToListing();
    },
    onError: () => {
      setErrorMessage(t('admin->>UserGroups.PendingRequestError'));
      setApiButtonStatus('error');
    },
  });

  const { mutate: postRequestFilePresigned } = usePostRequestFilePresigned({
    onSuccess: (data) => {
      const tempFiles: UploadedFile[] = [];
      data.forEach((_value, index) => {
        const pendingFile = files[index];
        const fileData = data[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        tempFiles.push(pendingFile);
      });
      setFiles(tempFiles);
    },
    onError: (err) => {
      setUploadFileErrorMessage(err?.response?.data.message!);
      setApiButtonStatus('error');
    },
  });

  useEffect(() => {
    mutatePostSearchApprovers({
      functionName: FUNCTION_NAMES.ADMIN_USER_GROUP,
      functionModule: FunctionModule.ADMIN,
    });
  }, [authorities, mutatePostSearchApprovers]);

  useEffect(() => {
    const isAllFilesUploaded = files.every((f) => f.uploaded);
    const fileKeys: string[] = [''];
    files.forEach((f) => {
      if (f.key) {
        fileKeys.push(f.key);
      }
    });

    const fileUploadHasError = files.some((f) => f.hasError);
    if (fileUploadHasError) {
      setApiButtonStatus('error');
      setShouldSubmit(false);
    }

    if (isAllFilesUploaded && shouldSubmit) {
      const deleteUserGroupPayload = {
        requestorRemarks: getValues('remarks'),
        approverUuid: getValues('approver')?.code,
        fileKeys,
      };
      mutateDeleteUserGroup(deleteUserGroupPayload);
    }
  }, [files, getValues, mutateDeleteUserGroup, shouldSubmit, uuid]);

  if (postSearchApproversIsLoading) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  const handleDeleteUserGroup = () => {
    if (errorMessage) {
      setConfirmChangesModal(false);
    } else {
      setConfirmChangesModal(true);
    }
  };

  const handleConfirmChangesModal = () => {
    const { filenames } = prepareFiles(files, setFiles);
    setShouldSubmit(true);
    setConfirmChangesModal(false);
    setApiButtonStatus('loading');

    if (filenames.length) {
      setConfirmChangesModal(false);
      postRequestFilePresigned({
        filenames,
        uuid,
      });
    }
  };

  return (
    <Dialog
      id="user-group-access-configuration-add-admin-dialog"
      fullWidth
      open={modalState}
      maxWidth="md"
    >
      <DialogTitle>
        {t('admin->>UserGroups.DeleteUserGroupName', {
          userGroupName: deleteUserGroupName,
        })}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleDeleteUserGroup)}>
        <DialogContent>
          <Typography className={classes.statementApproverRemarksField}>
            {t('admin->>UserGroups.DeleteUserGroupStatement')}
          </Typography>
          <ChangeRequestForm
            formHook={formHook}
            approverDropdowns={mapApproversToDropdown(postSearchApproversData)}
            isFullWidth
          />
          {requestFilesConfigData && (
            <Grid container spacing={0}>
              <Grid md={12} sm={12} xs={12}>
                <RequestFileDropZone
                  fileConfig={requestFilesConfigData}
                  files={files}
                  setFiles={setFiles}
                  isLoading={apiButtonStatus === 'loading'}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        {errorMessage && (
          <Typography className={classes.errorText} variant="body2">
            {errorMessage}
          </Typography>
        )}
        {uploadFileErrorMessage && (
          <Typography className={classes.errorText} variant="body2">
            {uploadFileErrorMessage}
          </Typography>
        )}
        <DialogActions>
          <Box className={classes.buttonContainer}>
            <Button
              id="button-delete-user-group-cancel"
              type="button"
              variant="text"
              onClick={handleCloseDeleteUserGroupModal}
            >
              {t('admin->>UserGroups.Cancel')}
            </Button>
            <ApiButton id="button-delete-user-group-proceed" status={apiButtonStatus} type="submit">
              {t('admin->>UserGroups.Proceed')}
            </ApiButton>
          </Box>
        </DialogActions>
      </form>
      <ChangeRequestDialog
        open={confirmChangesModal}
        onClose={() => setConfirmChangesModal(false)}
        onConfirm={handleConfirmChangesModal}
      />
    </Dialog>
  );
};

export default DeleteUserGroupModal;
